/**
 * All fiat amounts are integer minor units (pence). Token amounts are bigint
 * in the token's native decimals. Never do float arithmetic on money.
 */

export function formatPence(pence: number, currency = "GBP"): string {
  const sign = pence < 0 ? "-" : "";
  const abs = Math.abs(pence);
  const major = Math.floor(abs / 100);
  const minor = abs % 100;
  const symbol = currency === "GBP" ? "£" : currency === "USD" ? "$" : `${currency} `;
  return `${sign}${symbol}${major}.${String(minor).padStart(2, "0")}`;
}

/** Short form for small amounts: 58p instead of £0.58 (GBP only). */
export function formatPenceShort(pence: number, currency = "GBP"): string {
  if (currency === "GBP" && pence >= 0 && pence < 100) return `${pence}p`;
  return formatPence(pence, currency);
}

/** Convert an integer amount of minor units into token base units. */
export function penceToTokenUnits(pence: number, tokenDecimals: number, gbpPerToken = 1): bigint {
  // pence / 100 / gbpPerToken tokens → base units, computed in integer space.
  // gbpPerToken is passed as a price with 6 implied decimals via priceE6 helpers
  // where precision matters; this simple path assumes 1:1 pegs (e.g. GBPe).
  const scale = 10n ** BigInt(tokenDecimals);
  return (BigInt(pence) * scale) / (100n * BigInt(Math.round(gbpPerToken)));
}

/** Format a bigint token amount for display. */
export function formatTokenUnits(units: bigint, decimals: number, maxDp = 6): string {
  const scale = 10n ** BigInt(decimals);
  const whole = units / scale;
  const frac = units % scale;
  if (frac === 0n) return whole.toString();
  let fracStr = frac.toString().padStart(decimals, "0").slice(0, maxDp);
  fracStr = fracStr.replace(/0+$/, "");
  return fracStr ? `${whole}.${fracStr}` : whole.toString();
}

/**
 * Convert GBP pence to USD cents using a rate expressed as micro-USD per GBP
 * (e.g. 1.27 USD/GBP → 1_270_000). Integer arithmetic, floor rounding so we
 * never overstate what the user's round-up is worth.
 */
export function gbpPenceToUsdCents(pence: number, usdPerGbpE6: number): number {
  return fiatMinorToUsdCents(pence, usdPerGbpE6);
}

/** Currency minor units → USD cents, given micro-USD per major unit. */
export function fiatMinorToUsdCents(minor: number, usdPerCurrencyE6: number): number {
  return Number((BigInt(minor) * BigInt(usdPerCurrencyE6)) / 1_000_000n);
}

/** USD cents → currency minor units, rounded down to avoid overstating value. */
export function usdCentsToFiatMinor(cents: number, usdPerCurrencyE6: number): number {
  if (usdPerCurrencyE6 <= 0) throw new Error("FX rate must be positive");
  return Number((BigInt(cents) * 1_000_000n) / BigInt(usdPerCurrencyE6));
}

/** USD cents → token base units for a USD-pegged token (USDG/USDC). */
export function usdCentsToTokenUnits(cents: number, tokenDecimals: number): bigint {
  return (BigInt(cents) * 10n ** BigInt(tokenDecimals)) / 100n;
}

export function tokenUnitsToUsdCents(units: bigint, tokenDecimals: number): number {
  return Number((units * 100n) / 10n ** BigInt(tokenDecimals));
}
